/**
 * Language Auto-Detect Toast
 * Shows a friendly notification when the app automatically detects the user's browser language
 */
import React from "react";
import { Globe, X, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const LanguageAutoDetectToast = () => {
  const { 
    autoDetected, 
    detectedLanguageName, 
    language,
    languageNames,
    dismissAutoDetected 
  } = useLanguage();

  if (!autoDetected) return null;

  const currentLangInfo = languageNames[language] || { name: "English", flag: "🇺🇸" };

  return (
    <div 
      className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-5 fade-in duration-300"
      data-testid="language-auto-detect-toast"
    >
      <div className="bg-slate-900/95 backdrop-blur-xl border border-cyan-500/30 rounded-2xl shadow-2xl shadow-cyan-500/10 px-5 py-4 max-w-sm">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
            <Globe className="w-5 h-5 text-white" />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{currentLangInfo.flag}</span>
              <p className="text-white font-semibold text-sm">
                {detectedLanguageName} Detected
              </p>
            </div>
            <p className="text-slate-400 text-xs leading-relaxed">
              We've set your language based on your browser settings. You can change it anytime using the language selector.
            </p>
          </div>
          
          {/* Close button */}
          <button
            onClick={dismissAutoDetected}
            className="text-slate-500 hover:text-white transition-colors p-1 -mr-1 -mt-1"
            aria-label="Dismiss"
            data-testid="dismiss-language-toast"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Success indicator */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
          <Check className="w-4 h-4 text-emerald-400" />
          <span className="text-emerald-400 text-xs font-medium">
            Language set to {currentLangInfo.name}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LanguageAutoDetectToast;
